import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { Box, Breadcrumbs } from "@mui/material";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import * as Yup from "yup";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "../../../components/TextField";
import GridTable from "../../../components/GridTable";
import ActionMenu from "../../../components/ActionMenu";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import { successMessage, errorMessage } from "../../../components/Toaster";
import {
    getVarietiesByCropId,
    createVariety,
    updateVariety,
    deleteVariety,
} from "./api";
import { VarietyData, CreateVarietyParams, ApiResponse } from "./interface";
import { getErrorMessage } from "../../../utils/error";

const Variety: React.FC = () => {
    const { crop_id } = useParams();
    const location = useLocation();
    const crop_name = location.state?.crop_name || "Variety";
    const [varieties, setVarieties] = useState<VarietyData[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [editVariety, setEditVariety] = useState<VarietyData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        update: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        varietyName: Yup.string().required("Variety name is required"),
    });

    const columns: GridColDef[] = [
        { field: "variety_name", headerName: "Variety Name", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: () => handleEdit(params),
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                    ]}
                />
            ),
        },
    ];

    useEffect(() => {
        const fetchVarieties = async () => {
            if (!crop_id) return;
            setLoading((prev) => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<VarietyData[]> = await getVarietiesByCropId(crop_id);
                if (res.status && res.data) {
                    setVarieties(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch varieties.");
                }
            } catch (err) {
                errorMessage(getErrorMessage(err) || "Error fetching varieties.");
            } finally {
                setLoading((prev) => ({ ...prev, fetch: false }));
            }
        };

        fetchVarieties();
    }, [crop_id]);

    const openDialog = () => setDialogOpen(true);
    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditVariety(null);
        setDialogOpen(false);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditVariety(params.row as VarietyData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setItemToDelete(null);
        setDeleteOpen(false);
    };

    const confirmDialog = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        let success = false;
        if (editVariety) {
            success = await handleUpdate(values);
        } else {
            success = await handleCreate(values);
        }
        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: { varietyName: string }) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            const newVariety: CreateVarietyParams = {
                variety_name: values.varietyName,
                crop_id: crop_id!,
            };
            const res = await createVariety(newVariety);
            if (res.status && res.data) {
                const newVariety = {
                    variety_id: res.data!.variety_id,
                    crop_id: crop_id!,
                    variety_name: values.varietyName
                };
                setVarieties((prev) => [...prev, newVariety]);
                successMessage(res.message);
                return true;
            } else {
                errorMessage(res.message || "Failed to create variety.");
                return false;
            }
        } catch (err) {
            errorMessage(getErrorMessage(err) || "Unexpected error.");
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: { varietyName: string }) => {
        setLoading((prev) => ({ ...prev, update: true }));
        try {
            const updateData = {
                variety_name: values.varietyName.trim(),
                variety_id: editVariety?.variety_id!,
                crop_id: crop_id!,
            };
            const res = await updateVariety(updateData);
            if (res.status) {
                setVarieties((prev) =>
                    prev.map((v) =>
                        v.variety_id === editVariety?.variety_id
                            ? { ...v, ...updateData }
                            : v
                    )
                );
                successMessage(res.message || "Variety updated successfully");
                return true;
            } else {
                errorMessage(res.message || "Failed to update variety");
                return false;
            }
        } catch (err) {
            errorMessage(getErrorMessage(err) || "Unexpected error");
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, update: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));
        try {
            if (itemToDelete) {
                const res = await deleteVariety(itemToDelete.row.variety_id);
                if (res.status) {
                    setVarieties((prev) =>
                        prev.filter((v) => v.variety_id !== itemToDelete.row.variety_id)
                    );
                    successMessage(res.message);
                } else {
                    errorMessage(res.message || "Failed to delete variety");
                }
            }
        } catch (err) {
            errorMessage(getErrorMessage(err) || "Unexpected error");
        } finally {
            setLoading((prev) => ({ ...prev, delete: false }));
            setDeleteOpen(false);
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box>
                    <main className="m-a-20">
                        <div
                            style={{ display: "flex", alignItems: "center", marginBottom: "30px" }}
                        >
                            <Breadcrumbs
                                separator={<NavigateNextIcon fontSize="small" />}
                                aria-label="breadcrumb"
                                style={{ fontSize: "18px", fontWeight: "bold" }}
                            >
                                <Link color="primary" to="/crops">
                                    Crops
                                </Link>
                                <Link color="primary" to={`/crops/${crop_id}/varieties`} state={{ crop_name: crop_name }}>{crop_name}</Link>
                            </Breadcrumbs>
                        </div>
                        <div className="d-flex justify-end">
                            <div className="d-flex justify-between w-100">
                                <TextField
                                    className="w-25"
                                    label="Search"
                                    variant="outlined"
                                    value={searchQuery}
                                    size="small"
                                    startIcon={<SearchIcon />}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <Button
                                    label="Add"
                                    variant="contained"
                                    color="primary"
                                    onClick={openDialog}
                                />
                            </div>
                        </div>
                        <div className="ag-theme-alpine m-t-20" style={{ height: "100%", width: "100%" }}>
                            <GridTable
                                rows={varieties}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.variety_id}
                            />
                        </div>
                        <Formik
                            initialValues={{
                                varietyName: editVariety?.variety_name || "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({ values, errors, touched, handleChange, handleSubmit, resetForm }) => (
                                <Dialog
                                    open={isDialogOpen}
                                    onClose={() => closeDialog(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={editVariety ? "Edit Variety" : "Add New Variety"}
                                    confirmButtonText={editVariety ? "Update" : "Add"}
                                    cancelButtonText="Cancel"
                                    icon={<LocalFloristIcon />}
                                    message={
                                        <Box padding={4}>
                                            {loading.update || loading.create ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={12}>
                                                            <TextField
                                                                id="varietyName"
                                                                name="varietyName"
                                                                label="Variety Name"
                                                                variant="outlined"
                                                                fullWidth
                                                                value={values.varietyName}
                                                                onChange={handleChange}
                                                                required
                                                                error={Boolean(touched.varietyName && errors.varietyName)}
                                                                helperText={touched.varietyName && errors.varietyName}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                    </main>
                </Box>
            )}
            <Dialog
                open={deleteOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={!loading.delete ? "Delete Variety" : ""}
                confirmButtonText={!loading.delete ? "Confirm" : ""}
                cancelButtonText={!loading.delete ? "Cancel" : ""}
                icon={<DeleteIcon />}
                message={
                    <Box padding={2}>
                        {loading.delete ? (
                            <Loader color="primary" size={40} />
                        ) : (
                            "Are you sure you want to delete this variety?"
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default Variety;
